import React from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';

interface Props {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** How many documents ticking this would add. Hidden when nothing is being withheld. */
  withheldCount: number;
  disabled?: boolean;
}

/**
 * SQ-181: "Include documents held from billing".
 *
 * Lives with the documents — step 3 on both panels — not with the packaging. It changes
 * *which documents exist* to choose from, and on By patient the user ticks individual
 * documents in that step: a flag applied after the list is drawn cannot bring a hidden
 * document back. THIN-8113 puts the same control in Filters for batch print.
 *
 * The count is the point. "Include documents held from billing" with no number beside it
 * asks the user to guess whether it matters; with one, the decision is a decision.
 *
 * Deliberately **not** remembered between requests — see the panels, which hold this in
 * ordinary state rather than anything that persists. THIN-8113: "Do not store this in
 * localStorage or user preferences." An internal review must not silently set up the next
 * external submission to over-disclose.
 *
 * Compliance holds are not mentioned here at all. No toggle brings one back, so naming
 * them would offer a choice that does not exist.
 */
export const HeldFromBillingToggle: React.FC<Props> = ({
  checked,
  onChange,
  withheldCount,
  disabled = false,
}) => {
  //  Nothing held and nothing included: the control would be a switch with no effect, and
  //  a dead control is worse than no control. It reappears the moment there is something
  //  to decide.
  if (!checked && withheldCount === 0) return null;

  return (
    <Box
      sx={{
        mt: 2,
        p: 1.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: checked ? '#6366F1' : '#E0E0E0',
        backgroundColor: checked ? '#EEF2FF' : 'transparent',
      }}
    >
      <FormControlLabel
        sx={{ alignItems: 'flex-start', ml: 0 }}
        control={
          <Checkbox
            size="small"
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
            inputProps={{ 'aria-label': 'Include documents held from billing' }}
          />
        }
        label={
          <Box>
            <Typography
              variant="body2"
              color="text.primary"
              fontWeight={500}
            >
              Also include documents held from billing
              {!checked && withheldCount > 0 ? ` (${withheldCount})` : ''}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
            >
              Excluded by default. Include them for internal review — not for records going to an
              outside auditor or payer.
            </Typography>
          </Box>
        }
      />
    </Box>
  );
};

export default HeldFromBillingToggle;
